import { Form, Formik } from 'formik';
import React, { useState, useEffect } from 'react';
import { Alert } from 'react-bootstrap';
import { LockFill, PersonFill } from 'react-bootstrap-icons';
import { Link, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import loginImg from 'src/assets/images/login.png';
import { useAppDispatch } from 'src/hooks';
import { login } from 'src/reducers/user';
import { UserSelectors } from 'src/reducers/user';
import { Button, Input, Text } from 'src/shared';
import { Size } from 'src/shared/text';
import { getAuthToken, setAuthToken } from 'src/utils/auth';
import { Colors } from 'src/utils/color';
import { safeLog, safeErrorLog } from 'src/utils/safeLogging';
import TermsAndConditionsModal from 'src/components/TermsAndConditionsModal';
import { setTermsAgreementStatus, hasUserAgreedToTerms } from 'src/utils/termsAgreement';
import { useTermsAgreement } from 'src/hooks/useTermsAgreement';
import { verboseLog } from 'src/utils/config';
import MFAVerifyModal from 'src/components/MFAVerifyModal';
import MFASetupModal from 'src/components/MFASetupModal';

import {
    Bottomcontent,
    Bottomterms,
    Cardbodyimg,
    Cardform,
    ForgotPassword,
    Wrapper,
    SignupSection
} from './styles';

const Login = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { profile } = UserSelectors();
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [hasAgreedToTerms, setHasAgreedToTerms] = useState(() => hasUserAgreedToTerms());
    const { hasAgreed: globalTermsAgreed, refreshAgreementStatus } = useTermsAgreement();
    
    const validationSchema = yup.object().shape({
        email: yup
            .string()
            .required('Email is required')
            .email('Invalid email'),
        password: yup.string().required('Password is required')
    });
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [showMFAVerify, setShowMFAVerify] = useState(false);
    const [showMFASetup, setShowMFASetup] = useState(false);
    const [loginEmail, setLoginEmail] = useState<string>('');

    const onSubmit = async (data: any) => {
        // DEBUG - Safe logging (mask password)
        safeLog("🟡 Form submitted!", data);
        setErrorMessage('');
        setLoginEmail(data.email);
        
        dispatch(
            login({
                email: data.email,
                password: data.password
            })
        )
            .unwrap()
            .then((response: any) => {
                if (response?.success) {
                    setAuthToken(response?.data?.auth_token);
                    
                    // Check if MFA setup is required
                    if (response?.data?.mfa_setup_required || response?.code === 'mfa_setup_required') {
                        // User logged in but needs to set up MFA
                        // Store flag in sessionStorage so landing page can show modal
                        sessionStorage.setItem('mfa_setup_required', 'true');
                        verboseLog('⚠️ Login successful but MFA setup required - flag stored in sessionStorage');
                        // Don't show modal here - let landing page handle it
                        // Navigate to landing page where modal will be shown
                        navigate('/');
                    } else {
                        // Normal successful login
                        // Clear any MFA setup flag
                        sessionStorage.removeItem('mfa_setup_required');
                        // Close login modal/page and stay on current page
                        // User state will be updated in Redux automatically
                        window.history.back(); // Go back to previous page
                    }
                } else {
                    // Check if MFA verification is required
                    if (response?.code === 'mfa_verification_required' || response?.data?.mfa_verification_required) {
                        setShowMFAVerify(true);
                    } else {
                        setErrorMessage(response?.message || 'Login failed. Please try again.');
                    }
                }
            })
            .catch((err: any) => {
                // DEBUG
                safeErrorLog('Login failed', err);
                
                // Check if MFA verification is required in error response
                if (err?.data?.code === 'mfa_verification_required' || err?.data?.data?.mfa_verification_required) {
                    setShowMFAVerify(true);
                } else {
                    setErrorMessage(err?.data?.message || 'Something went wrong. Please try again.');
                }
            });
    };

    const handleMFASuccess = () => {
        setShowMFAVerify(false);
        // User state will be updated in Redux automatically
        window.history.back(); // Go back to previous page
    };

    const handleMFASetupComplete = () => {
        setShowMFASetup(false);
        // MFA setup is complete, user can now use the system
        // Refresh the page or navigate to home
        window.location.href = '/';
    };

    const handleTermsClick = (e: React.MouseEvent) => {
        e.preventDefault();
        setShowTermsModal(true);
    };

    const handleAgreeToTerms = () => {
        setHasAgreedToTerms(true);
        setTermsAgreementStatus(true);
        refreshAgreementStatus(); // Refresh the global hook state
    };

    // Check if user is actually logged in (both token AND valid profile must exist)
    const authToken = getAuthToken();
    const isLoggedIn = authToken && profile;
    verboseLog('🔵 Login component mounted, authToken exists:', !!authToken, 'profile exists:', !!profile, 'isLoggedIn:', isLoggedIn);
    
    // Only redirect if user is actually logged in with valid profile
    // If token exists but no profile, token might be invalid/expired - show login page
    useEffect(() => {
        if (isLoggedIn) {
            verboseLog('⚠️ User already logged in with valid profile, redirecting back');
            // Use navigate instead of window.history.back() for better control
            navigate(-1);
        }
    }, [isLoggedIn, navigate]);

    if (authToken && !profile) {
        verboseLog('⚠️ Token exists but no profile - token may be invalid/expired, showing login page');
    }
    
    // Don't render login form if already logged in
    if (isLoggedIn) {
        return null;
    }

    verboseLog('✅ Rendering login form');
    return (
        <div className="main-row">
            <Wrapper>
                <div className="row">
                    <div className="col-lg-12 col-md-12 col-sm-12">
                        <div className="card">
                            <div className="card-header">
                                <Text
                                    padding={8}
                                    fontSize={Size.Medium}
                                    fontWeight="bold"
                                    color="white"
                                >
                                    Login
                                </Text>
                            </div>
                            <Cardform>
                                <Cardbodyimg>
                                    <img src={loginImg} alt="login-logo" />
                                </Cardbodyimg>
                                <div className="row">
                                    <Formik
                                        initialValues={{
                                            email: '',
                                            password: ''
                                        }}
                                        validationSchema={validationSchema}
                                        onSubmit={onSubmit}
                                        validateOnMount={false}
                                    >
                                        {({
                                            values,
                                            handleChange,
                                            handleSubmit,
                                            errors,
                                            touched
                                        }) => (
                                            <Form onSubmit={handleSubmit}>
                                                {errorMessage && (
                                                    <Alert variant="danger">
                                                        {errorMessage}
                                                    </Alert>
                                                )}
                                                <Input
                                                    label="Email"
                                                    icon={PersonFill}
                                                    onChange={handleChange(
                                                        'email'
                                                    )}
                                                    type="text"
                                                    name="email"
                                                    value={values.email}
                                                />
                                                {touched.email &&
                                                    errors.email && (
                                                        <Text
                                                            marginLeft={50}
                                                            marginBottom={0}
                                                            color={
                                                                Colors.extra.red
                                                            }
                                                        >
                                                            {errors.email}
                                                        </Text>
                                                    )}
                                                <Input
                                                    label="Password"
                                                    icon={LockFill}
                                                    onChange={handleChange(
                                                        'password'
                                                    )}
                                                    type="password"
                                                    value={values.password}
                                                />
                                                {touched.password &&
                                                    errors.password && (
                                                        <Text
                                                            marginLeft={50}
                                                            marginBottom={0}
                                                            color={
                                                                Colors.extra.red
                                                            }
                                                        >
                                                            {errors.password}
                                                        </Text>
                                                    )}
                                                
                                                {/* Forgot Password - positioned above login button */}
                                                <ForgotPassword>
                                                    <Link to="/forgot-password">
                                                        Forgot Password?
                                                    </Link>
                                                </ForgotPassword>

                                                <Bottomcontent>
                                                    <Bottomterms>
                                                        <Text>
                                                            By logging in, you
                                                            agree to our{' '}
                                                            <button
                                                                type="button"
                                                                className="tnc-link"
                                                                onClick={handleTermsClick}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    color: globalTermsAgreed ? '#10b981' : '#3b82f6',
                                                                    textDecoration: 'underline',
                                                                    cursor: 'pointer',
                                                                    padding: 0,
                                                                    font: 'inherit'
                                                                }}
                                                            >
                                                                terms and
                                                                conditions.
                                                                {globalTermsAgreed && ' ✓'}
                                                            </button>
                                                        </Text>
                                                    </Bottomterms>
                                                    <div className="btn-sec">
                                                        <Button>LOGIN</Button>
                                                    </div>
                                                </Bottomcontent>

                                                {/* Signup Section - centered */}
                                                <SignupSection>
                                                    <Text>
                                                        Don't have an account?{' '}
                                                        <Link 
                                                            to={globalTermsAgreed ? "/signup" : "#"}
                                                            onClick={globalTermsAgreed ? undefined : (e) => {
                                                                e.preventDefault();
                                                                setShowTermsModal(true);
                                                            }}
                                                            style={{
                                                                color: globalTermsAgreed ? '#3b82f6' : '#9ca3af',
                                                                textDecoration: globalTermsAgreed ? 'underline' : 'none',
                                                                cursor: globalTermsAgreed ? 'pointer' : 'not-allowed'
                                                            }}
                                                        >
                                                            Sign up here
                                                        </Link>
                                                    </Text>
                                                    {!globalTermsAgreed && (
                                                        <Text
                                                            fontSize={Size.X5Small}
                                                            color="#ef4444"
                                                            marginTop={5}
                                                            fontFamily="Inter"
                                                        >
                                                            You must agree to terms and conditions first
                                                        </Text>
                                                    )}
                                                </SignupSection>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </Cardform>
                        </div>
                    </div>
                </div>
            </Wrapper>

            {/* Terms and Conditions Modal */}
            <TermsAndConditionsModal
                show={showTermsModal}
                onHide={() => setShowTermsModal(false)}
                onAgree={handleAgreeToTerms}
            />

            {/* MFA Verification Modal */}
            <MFAVerifyModal
                show={showMFAVerify}
                onHide={() => setShowMFAVerify(false)}
                email={loginEmail}
                onSuccess={handleMFASuccess}
            />

            {/* MFA Setup Modal - Shown when user logs in but hasn't set up MFA yet */}
            <MFASetupModal
                show={showMFASetup}
                onHide={() => {
                    setShowMFASetup(false);
                    // If user cancels MFA setup, log them out
                    setAuthToken('');
                    window.location.href = '/login';
                }}
                onComplete={handleMFASetupComplete}
            />
        </div>
    );
};

export default Login;
